import { categories } from "@/mocks/categories";
import { videos } from "@/mocks/videos";
import type { Category, Video, VideoType } from "@/types/video";

export const PAGE_SIZE = 6;

export const VIDEO_TYPES: VideoType[] = ["剧情短片", "视觉写真", "精选合集", "幕后访谈"];

export type DurationFilter = "all" | "short" | "medium" | "long";
export type DateFilter = "all" | "7" | "30" | "90";
export type SortOption = "latest" | "popular";

export interface FilterState {
  type: VideoType | "all";
  duration: DurationFilter;
  date: DateFilter;
  sort: SortOption;
}

export const DEFAULT_FILTERS: FilterState = {
  type: "all",
  duration: "all",
  date: "all",
  sort: "latest",
};

export const DURATION_OPTIONS: { value: DurationFilter; label: string }[] = [
  { value: "all", label: "全部时长" },
  { value: "short", label: "10 分钟以内" },
  { value: "medium", label: "10 - 15 分钟" },
  { value: "long", label: "15 分钟以上" },
];

export const DATE_OPTIONS: { value: DateFilter; label: string }[] = [
  { value: "all", label: "全部日期" },
  { value: "7", label: "最近 7 天" },
  { value: "30", label: "最近 30 天" },
  { value: "90", label: "最近三个月" },
];

export function getCategory(slug?: string): Category | undefined {
  if (!slug) return undefined;
  return categories.find((item) => item.slug === slug);
}

export function getCategoryName(slug: string): string {
  return getCategory(slug)?.name ?? "未分类";
}

export function getVideoById(id?: string): Video | undefined {
  if (!id) return undefined;
  return videos.find((item) => item.id === id);
}

export function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  return `${minutes}:${String(rest).padStart(2, "0")}`;
}

export function formatViews(views: number): string {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)} 万次播放`;
  }
  return `${views} 次播放`;
}

export function formatCompactViews(views: number): string {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)}万`;
  }
  return `${views}`;
}

export function formatDate(iso: string): string {
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return iso;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;
}

export function formatShortDate(iso: string): string {
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return iso;
  return `${date.getMonth() + 1} 月 ${date.getDate()} 日`;
}

export function sortByLatest(list: Video[]): Video[] {
  return [...list].sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1));
}

export function getLatestVideos(limit: number): Video[] {
  return sortByLatest(videos).slice(0, limit);
}

export function getPopularVideos(limit: number): Video[] {
  return [...videos].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function getCategoryVideos(slug: string): Video[] {
  return videos.filter((item) => item.category === slug);
}

export function getCategoryCount(slug: string): number {
  return getCategoryVideos(slug).length;
}

export function getRelatedVideos(video: Video, limit = 4): Video[] {
  const scored = videos
    .filter((item) => item.id !== video.id)
    .map((item) => {
      const sharedTags = item.tags.filter((tag) => video.tags.includes(tag)).length;
      const sameCategory = item.category === video.category ? 2 : 0;
      const sameType = item.type === video.type ? 1 : 0;
      return { item, score: sharedTags * 3 + sameCategory + sameType };
    })
    .sort((a, b) => b.score - a.score || b.item.views - a.item.views);

  return scored.slice(0, limit).map((entry) => entry.item);
}

function matchDuration(video: Video, filter: DurationFilter): boolean {
  if (filter === "all") return true;
  if (filter === "short") return video.duration < 600;
  if (filter === "medium") return video.duration >= 600 && video.duration < 900;
  return video.duration >= 900;
}

function matchDate(video: Video, filter: DateFilter): boolean {
  if (filter === "all") return true;
  const published = new Date(`${video.publishedAt}T00:00:00`).getTime();
  const days = (Date.now() - published) / 86400000;
  return days <= Number(filter);
}

export function applyFilters(list: Video[], filters: FilterState): Video[] {
  const filtered = list.filter(
    (video) =>
      (filters.type === "all" || video.type === filters.type) &&
      matchDuration(video, filters.duration) &&
      matchDate(video, filters.date),
  );

  return filters.sort === "popular"
    ? [...filtered].sort((a, b) => b.views - a.views)
    : sortByLatest(filtered);
}

export function searchVideos(keyword: string): Video[] {
  const query = keyword.trim().toLowerCase();
  if (!query) return [];
  return sortByLatest(
    videos.filter((video) => {
      const categoryName = getCategoryName(video.category).toLowerCase();
      return (
        video.title.toLowerCase().includes(query) ||
        video.summary.toLowerCase().includes(query) ||
        video.category.toLowerCase().includes(query) ||
        categoryName.includes(query) ||
        video.tags.some((tag) => tag.toLowerCase().includes(query))
      );
    }),
  );
}

export function paginate<T>(list: T[], page: number, pageSize = PAGE_SIZE): T[] {
  const safePage = Math.max(1, Math.min(page, Math.max(1, Math.ceil(list.length / pageSize))));
  const start = (safePage - 1) * pageSize;
  return list.slice(start, start + pageSize);
}

export function getTotalPages(total: number, pageSize = PAGE_SIZE): number {
  return Math.max(1, Math.ceil(total / pageSize));
}